import { ProductInventoryUnit } from '@gooduncles/gu-app-schema';

export interface SupplierInboundItem {
  id: string;
  /** `${inventoryId}-${unit}` */
  key: string;
  inventoryId: string;
  inventoryName: string;
  unit: ProductInventoryUnit;
  requestValue: number;
}

// 액션 타입 정의
export enum SupplierInboundActionTypes {
  ADD_ITEM = 'ADD_ITEM',
  ADD_ITEMS = 'ADD_ITEMS',
  UPDATE_ITEM = 'UPDATE_ITEM',
  REMOVE_ITEM = 'REMOVE_ITEM',
  CLEAR_ITEMS = 'CLEAR_ITEMS',
  SET_ITEMS = 'SET_ITEMS',
}

// 액션 인터페이스 정의
interface AddItemAction {
  type: SupplierInboundActionTypes.ADD_ITEM;
  payload: SupplierInboundItem;
}

interface AddItemsAction {
  type: SupplierInboundActionTypes.ADD_ITEMS;
  payload: SupplierInboundItem[];
}

interface UpdateItemAction {
  type: SupplierInboundActionTypes.UPDATE_ITEM;
  payload: {
    id: string;
    data: Partial<SupplierInboundItem>;
  };
}

interface RemoveItemAction {
  type: SupplierInboundActionTypes.REMOVE_ITEM;
  payload: string; // id
}

interface ClearItemsAction {
  type: SupplierInboundActionTypes.CLEAR_ITEMS;
}

interface SetItemsAction {
  type: SupplierInboundActionTypes.SET_ITEMS;
  payload: SupplierInboundItem[];
}

type SupplierInboundAction =
  | AddItemAction
  | AddItemsAction
  | UpdateItemAction
  | RemoveItemAction
  | ClearItemsAction
  | SetItemsAction;

// 액션 작성자 함수
export const addItem = (item: SupplierInboundItem): AddItemAction => ({
  type: SupplierInboundActionTypes.ADD_ITEM,
  payload: item,
});

export const addItems = (items: SupplierInboundItem[]): AddItemsAction => ({
  type: SupplierInboundActionTypes.ADD_ITEMS,
  payload: items,
});

export const updateItem = (id: string, data: Partial<SupplierInboundItem>): UpdateItemAction => ({
  type: SupplierInboundActionTypes.UPDATE_ITEM,
  payload: { id, data },
});

export const removeItem = (id: string): RemoveItemAction => ({
  type: SupplierInboundActionTypes.REMOVE_ITEM,
  payload: id,
});

export const clearItems = (): ClearItemsAction => ({
  type: SupplierInboundActionTypes.CLEAR_ITEMS,
});

export const setItems = (items: SupplierInboundItem[]): SetItemsAction => ({
  type: SupplierInboundActionTypes.SET_ITEMS,
  payload: items,
});

// 초기 상태 정의
export interface SupplierInboundState {
  items: SupplierInboundItem[];
}

const initialState: SupplierInboundState = {
  items: [],
};

// 리듀서 함수
export const supplierInboundReducer = (
  state: SupplierInboundState = initialState,
  action: SupplierInboundAction
): SupplierInboundState => {
  switch (action.type) {
    case SupplierInboundActionTypes.ADD_ITEM:
      return {
        ...state,
        items: [
          ...state.items,
          {
            ...action.payload,
          },
        ],
      };

    case SupplierInboundActionTypes.ADD_ITEMS:
      return {
        ...state,
        items: [
          ...state.items,
          ...action.payload.map((item) => ({
            ...item,
          })),
        ],
      };

    case SupplierInboundActionTypes.UPDATE_ITEM:
      return {
        ...state,
        items: state.items.map((item) => {
          if (item.id === action.payload.id) {
            return { ...item, ...action.payload.data };
          }
          return item;
        }),
      };

    case SupplierInboundActionTypes.REMOVE_ITEM:
      return {
        ...state,
        items: state.items.filter((item) => item.id !== action.payload),
      };

    case SupplierInboundActionTypes.CLEAR_ITEMS:
      return {
        ...state,
        items: [],
      };

    case SupplierInboundActionTypes.SET_ITEMS:
      return {
        ...state,
        items: action.payload,
      };

    default:
      return state;
  }
};

export const getItemById = (items: SupplierInboundItem[], id: string): SupplierInboundItem | undefined => {
  return items.find((item) => item.id === id);
};

export const getItemsByInventoryId = (items: SupplierInboundItem[], inventoryId: string): SupplierInboundItem[] => {
  return items.filter((item) => item.inventoryId === inventoryId);
};
